/**
 * Click-to-move board interaction.
 *
 * Kept here, away from the component, because "what should this click do?" has
 * more branches than it first appears — selecting, re-selecting a different
 * piece, moving, cancelling — and each one is worth a test.
 */
import { Chess, type Square } from 'chess.js'
import type { Fen, SquareName, UciMove } from '@shess/schemas'
import { toUci } from './chess.ts'

export interface LegalMove {
  to: SquareName
  /** Occupied target or en passant — drawn as a ring rather than a dot. */
  captures: boolean
  /** Present only for a promoting move; always a queen for now. */
  promotion?: 'q'
}

export type SquareClick =
  /** Show this piece as picked up, with `moves` as its destinations. */
  | { action: 'select'; square: SquareName; moves: LegalMove[] }
  /** Play it. */
  | { action: 'move'; from: SquareName; to: SquareName; move: UciMove }
  /** Drop the current selection. */
  | { action: 'deselect' }
  /** Nothing was selected and nothing can be, so leave the board alone. */
  | { action: 'ignore' }

/**
 * Where a piece may legally go.
 *
 * chess.js reports a promotion as four separate moves to the same square; they
 * collapse into one entry here because the board auto-queens.
 */
export function legalMovesFrom(fen: Fen, square: SquareName): LegalMove[] {
  const board = new Chess(fen)
  const seen = new Map<string, LegalMove>()

  for (const move of board.moves({ square: square as Square, verbose: true })) {
    if (seen.has(move.to)) continue
    seen.set(move.to, {
      to: move.to as SquareName,
      captures: Boolean(move.captured),
      ...(move.promotion ? { promotion: 'q' as const } : {}),
    })
  }

  return [...seen.values()]
}

/**
 * Decides what a click on `square` means, given what is currently selected.
 *
 * Clicking a piece selects it. Clicking it again, or clicking anywhere it
 * cannot go, cancels. Clicking a different piece of your own switches to it
 * rather than cancelling, which is what you almost always meant.
 */
export function resolveSquareClick(
  fen: Fen,
  selected: SquareName | null,
  square: SquareName,
): SquareClick {
  const board = new Chess(fen)
  const piece = board.get(square as Square)
  const isOwnPiece = piece !== undefined && piece !== null && piece.color === board.turn()

  if (selected === null) {
    return isOwnPiece
      ? { action: 'select', square, moves: legalMovesFrom(fen, square) }
      : { action: 'ignore' }
  }

  if (selected === square) return { action: 'deselect' }

  const target = legalMovesFrom(fen, selected).find((move) => move.to === square)
  if (target) {
    return {
      action: 'move',
      from: selected,
      to: square,
      move: toUci({ from: selected, to: square, ...(target.promotion ? { promotion: target.promotion } : {}) }),
    }
  }

  // Switching directly between your own pieces beats making the user cancel first.
  if (isOwnPiece) {
    return { action: 'select', square, moves: legalMovesFrom(fen, square) }
  }

  return { action: 'deselect' }
}
